import { useEffect, useMemo, useState, type ComponentProps, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { LoadingState } from '../common';
import DataTable from '../common/DataTable';
import { useEventContext } from '../../contexts/EventContext';
import { useEventCollectFlagsQuery } from '../../hooks/useEventCollectFlagsQuery';
import { useGraphCategoriesQuery } from '../../hooks/useGraphCategoriesQuery';
import { useGraphSplitsQuery } from '../../hooks/useGraphSplitsQuery';
import { useLeaderboardQuery } from '../../hooks/useLeaderboardQuery';
import { useSplitDistributionQuery } from '../../hooks/useSplitDistributionQuery';
import { useStoredRefreshInterval } from '../../hooks/useStoredRefreshInterval';
import { EVENT_LIST_ROUTE_PATH } from '../../routes/appRoutes';
import {
  type LeaderboardItem,
  type SplitDistributionSplit,
} from '../../services/graphApi';
import LeaderboardPrintDialog from './LeaderboardPrintDialog';
import { createLeaderboardColumns } from './leaderboardTableColumns';
import { StatisticsRefreshControl } from './StatisticsRefreshControl';

type LeaderboardFilters = {
  category: string;
  splitId: string;
  gender: string;
  ageGroup: string;
  group: string;
  bibKeyword: string;
};

const emptyFilters: LeaderboardFilters = {
  category: '',
  splitId: '',
  gender: '',
  ageGroup: '',
  group: '',
  bibKeyword: '',
};

const refreshOptions = [
  { label: '수동', value: 0 },
  { label: '10초', value: 10000 },
  { label: '30초', value: 30000 },
  { label: '1분', value: 60000 },
];
const LEADERBOARD_REFRESH_STORAGE_KEY = 'statistics.leaderboard.refreshIntervalMs';

function uniqueValues(values: string[]) {
  return Array.from(new Set(values.filter(Boolean))).sort((first, second) =>
    first.localeCompare(second, 'ko')
  );
}

function findDefaultSplitId(splits: SplitDistributionSplit[]) {
  const finishSplit = splits.find((split) => split.splitName.toLowerCase().includes('finish'));

  return finishSplit?.splitId ?? splits.at(-1)?.splitId;
}

function filterSplitsByCategory(splits: SplitDistributionSplit[], categoryName: string) {
  if (!categoryName) {
    return [];
  }

  return splits.filter((split) =>
    split.categoryName === categoryName && split.splitName.toLowerCase() !== 'start'
  );
}

function filterLeaderboardRows(rows: LeaderboardItem[], filters: LeaderboardFilters) {
  const bibKeyword = filters.bibKeyword.trim();

  return rows.filter((row) => {
    if (filters.category && row.category !== filters.category) {
      return false;
    }
    if (filters.gender && row.gender !== filters.gender) {
      return false;
    }
    if (filters.ageGroup && row.ageGroup !== filters.ageGroup) {
      return false;
    }
    if (filters.group && row.group !== filters.group) {
      return false;
    }
    if (bibKeyword && !String(row.bibNumber).includes(bibKeyword)) {
      return false;
    }

    return true;
  });
}

function FilterSelect({
  label,
  allLabel,
  value,
  options,
  disabled,
  onChange,
}: Readonly<{
  label: string;
  allLabel: string;
  value: string;
  options: ReadonlyArray<{ label: string; value: string }>;
  disabled?: boolean;
  onChange: (value: string) => void;
}>) {
  return (
    <select
      aria-label={label}
      className="leaderboard-filter-control"
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">{allLabel}</option>
      {options.map((option) => (
        <option value={option.value} key={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function LeaderboardDashboard() {
  const { selectedEventId, selectedEventName } = useEventContext();
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const collectFlagsQuery = useEventCollectFlagsQuery(selectedEventId);
  const collectBirth = collectFlagsQuery.data?.collectBirth ?? true;
  const collectGender = collectFlagsQuery.data?.collectGender ?? true;
  const [draftFilters, setDraftFilters] = useState<LeaderboardFilters>(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState<LeaderboardFilters>(emptyFilters);
  const selectedSplitId = Number(appliedFilters.splitId || draftFilters.splitId || 0);
  const {
    applyRefreshInterval,
    draftRefreshIntervalMs,
    refreshIntervalMs,
    setDraftRefreshIntervalMs,
  } = useStoredRefreshInterval(LEADERBOARD_REFRESH_STORAGE_KEY);
  const categoryQuery = useGraphCategoriesQuery(selectedEventId);
  const splitQuery = useGraphSplitsQuery(selectedEventId);
  const filterOptionQuery = useSplitDistributionQuery(selectedEventId);
  const selectedCategory = useMemo(
    () => (categoryQuery.data ?? []).find((category) => category.categoryName === appliedFilters.category) ?? null,
    [categoryQuery.data, appliedFilters.category]
  );
  const draftSelectedCategory = useMemo(
    () => (categoryQuery.data ?? []).find((category) => category.categoryName === draftFilters.category) ?? null,
    [categoryQuery.data, draftFilters.category]
  );
  // 백엔드 응답에 남아있는 값과 무관하게, 미수집 항목은 필터를 무조건 전체로 고정한다.
  const graphFilters = useMemo(() => ({
    gender: collectGender ? appliedFilters.gender : '',
    categoryId: selectedCategory?.id,
    ageGroup: collectBirth ? appliedFilters.ageGroup : '',
    group: appliedFilters.group,
  }), [
    appliedFilters.gender,
    appliedFilters.ageGroup,
    appliedFilters.group,
    selectedCategory?.id,
    collectBirth,
    collectGender,
  ]);
  const leaderboardQuery = useLeaderboardQuery(
    selectedEventId,
    selectedSplitId || undefined,
    refreshIntervalMs,
    graphFilters
  );
  const splits = useMemo(() => splitQuery.data ?? [], [splitQuery.data]);
  const rows = useMemo(
    () => leaderboardQuery.data?.leaderboards ?? [],
    [leaderboardQuery.data?.leaderboards]
  );
  const columns = useMemo(() => createLeaderboardColumns(), []);
  const categoryOptions = useMemo(
    () => {
      const categories = categoryQuery.data ?? [];

      if (categories.length > 0) {
        return categories.map((category) => ({
          label: category.categoryName,
          value: category.categoryName,
        }));
      }

      return uniqueValues(rows.map((row) => row.category)).map((value) => ({ label: value, value }));
    },
    [categoryQuery.data, rows]
  );
  // 성별·나이그룹 선택지는 표 조회 결과가 아니라 대회 전체 참가자(선택한 종목 기준)에서 만든다.
  const optionParticipants = useMemo(
    () => (filterOptionQuery.data?.participants ?? []).filter(
      (participant) => !draftFilters.category || participant.category === draftFilters.category
    ),
    [filterOptionQuery.data?.participants, draftFilters.category]
  );
  const genderOptions = useMemo(
    () => uniqueValues(optionParticipants.map((participant) => participant.gender))
      .map((value) => ({ label: value, value })),
    [optionParticipants]
  );
  const ageGroupOptions = useMemo(
    () => uniqueValues(optionParticipants.map((participant) => participant.ageGroup ?? ''))
      .map((value) => ({ label: value, value })),
    [optionParticipants]
  );
  const groupOptions = useMemo(
    () => {
      const groups = draftSelectedCategory?.groups?.map((group) => group.groupName) ?? [];
      return uniqueValues(groups).map((value) => ({ label: value, value }));
    },
    [draftSelectedCategory?.groups]
  );
  const categorySplits = useMemo(
    () => filterSplitsByCategory(splits, draftFilters.category),
    [splits, draftFilters.category]
  );
  const splitOptions = useMemo(
    () =>
      categorySplits.map((split) => ({
        label: split.splitName,
        value: String(split.splitId),
      })),
    [categorySplits]
  );
  const defaultSplitId = useMemo(() => findDefaultSplitId(categorySplits), [categorySplits]);
  const filteredRows = useMemo(
    () => filterLeaderboardRows(rows, appliedFilters),
    [rows, appliedFilters]
  );
  const updatedAt = Math.max(
    categoryQuery.dataUpdatedAt,
    splitQuery.dataUpdatedAt,
    leaderboardQuery.dataUpdatedAt
  );
  const error = splitQuery.error ?? leaderboardQuery.error;
  const isError = splitQuery.isError || leaderboardQuery.isError;
  const isLoading = splitQuery.isLoading || leaderboardQuery.isLoading;
  const isFetching = splitQuery.isFetching || leaderboardQuery.isFetching;
  const isSearchDisabled = isFetching || !draftFilters.category || !draftFilters.splitId;
  const isLeaderboardRefreshDisabled = isFetching || !draftFilters.category || !selectedSplitId;
  const errorMessage = error instanceof Error
    ? error.message
    : '리더보드 데이터를 불러오지 못했습니다.';

  useEffect(() => {
    if (!draftFilters.category || draftFilters.splitId || categorySplits.length === 0) {
      return;
    }

    if (!defaultSplitId) {
      return;
    }

    const nextSplitId = String(defaultSplitId);
    setDraftFilters((current) => ({ ...current, splitId: nextSplitId }));
    setAppliedFilters((current) => ({ ...current, splitId: nextSplitId }));
  }, [categorySplits.length, defaultSplitId, draftFilters.category, draftFilters.splitId]);

  const updateDraftFilter = (key: keyof LeaderboardFilters, value: string) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  };

  const applyFilterImmediately = (key: keyof LeaderboardFilters, value: string) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
    setAppliedFilters((current) => ({ ...current, [key]: value }));
  };

  const applyCategoryFilterImmediately = (categoryName: string) => {
    const nextCategorySplits = filterSplitsByCategory(splits, categoryName);
    const nextSplitId = findDefaultSplitId(nextCategorySplits);
    const nextFilters = {
      category: categoryName,
      splitId: nextSplitId ? String(nextSplitId) : '',
      group: '',
    };

    setDraftFilters((current) => ({ ...current, ...nextFilters }));
    setAppliedFilters((current) => ({ ...current, ...nextFilters }));
  };

  const handleSubmit: ComponentProps<'form'>['onSubmit'] = (event) => {
    event.preventDefault();
    if (!draftFilters.category || !draftFilters.splitId) {
      return;
    }

    setAppliedFilters((current) => ({ ...current, bibKeyword: draftFilters.bibKeyword }));
    void leaderboardQuery.refetch();
  };

  const handleResetFilters = () => {
    const resetFilters = {
      ...emptyFilters,
    };
    setDraftFilters(resetFilters);
    setAppliedFilters(resetFilters);
  };

  let tableContent: ReactNode = (
    <section className="leaderboard-table-panel" aria-busy={isFetching}>
      <DataTable
        columns={columns}
        rows={filteredRows}
        rowKey="participantId"
        emptyMessage="표시할 리더보드 데이터가 없습니다."
      />
    </section>
  );

  if (selectedEventId == null) {
    tableContent = (
      <div className="leaderboard-message">
        선택된 대회가 없습니다. <Link to={EVENT_LIST_ROUTE_PATH}>대회 목록에서 선택</Link>해 주세요.
      </div>
    );
  } else if (!draftFilters.category) {
    tableContent = (
      <div className="leaderboard-message">종목을 선택한 뒤 구간별 리더보드를 확인할 수 있습니다.</div>
    );
  } else if (splitOptions.length === 0) {
    tableContent = (
      <div className="leaderboard-message">선택한 종목에 등록된 구간이 없습니다.</div>
    );
  } else if (!draftFilters.splitId) {
    tableContent = (
      <div className="leaderboard-message">구간을 선택해 주세요.</div>
    );
  } else if (isLoading) {
    tableContent = <LoadingState message="리더보드 데이터를 불러오는 중입니다." />;
  }

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-title-row">
        <div className="page-title-block">
          <p className="page-section">Statistics</p>
          <h1>리더보드</h1>
          <p className="page-description">참가자 순위와 기록 차이를 실시간으로 확인합니다.</p>
        </div>
        <div className="leaderboard-title-actions">
          <button
            className="leaderboard-print-button"
            type="button"
            disabled={selectedEventId == null}
            onClick={() => setIsPrintDialogOpen(true)}
          >
            출력하기
          </button>
        <StatisticsRefreshControl
          ariaLabel="리더보드 갱신 주기"
          className="leaderboard-refresh-control"
          draftRefreshIntervalMs={draftRefreshIntervalMs}
          isApplyDisabled={draftRefreshIntervalMs === refreshIntervalMs}
          isRefreshDisabled={isLeaderboardRefreshDisabled}
          options={refreshOptions}
          updatedAt={updatedAt}
          onApply={applyRefreshInterval}
          onChange={setDraftRefreshIntervalMs}
          onRefresh={() => {
            if (!draftFilters.category || !selectedSplitId) {
              return;
            }

            void Promise.all([
              categoryQuery.refetch(),
              splitQuery.refetch(),
              leaderboardQuery.refetch(),
            ]);
          }}
        />
        </div>
      </div>

      <LeaderboardPrintDialog
        open={isPrintDialogOpen}
        eventId={selectedEventId}
        eventName={selectedEventName}
        categories={categoryQuery.data ?? []}
        splits={splits}
        ageGroupOptions={ageGroupOptions}
        genderOptions={genderOptions}
        collectBirth={collectBirth}
        collectGender={collectGender}
        initialValues={{
          category: draftFilters.category,
          splitId: draftFilters.splitId,
          group: draftFilters.group,
          ageGroup: draftFilters.ageGroup,
          gender: draftFilters.gender,
        }}
        onClose={() => setIsPrintDialogOpen(false)}
      />

      <form className="statistics-filter-row statistics-filter-row--leaderboard" onSubmit={handleSubmit}>
        <FilterSelect
          label="종목"
          allLabel="종목 선택"
          value={draftFilters.category}
          options={categoryOptions}
          disabled={isFetching}
          onChange={applyCategoryFilterImmediately}
        />
        <FilterSelect
          label="구간"
          allLabel="구간"
          value={draftFilters.splitId}
          options={splitOptions}
          disabled={isFetching || !draftFilters.category}
          onChange={(value) => applyFilterImmediately('splitId', value)}
        />
        <FilterSelect
          label="그룹"
          allLabel="그룹"
          value={draftFilters.group}
          options={groupOptions}
          disabled={isFetching || !draftFilters.category}
          onChange={(value) => applyFilterImmediately('group', value)}
        />
        <FilterSelect
          label="나이그룹"
          allLabel="나이그룹 전체"
          value={collectBirth ? draftFilters.ageGroup : ''}
          options={ageGroupOptions}
          disabled={!collectBirth || ageGroupOptions.length === 0}
          onChange={(value) => applyFilterImmediately('ageGroup', value)}
        />
        <FilterSelect
          label="성별"
          allLabel="성별 전체"
          value={collectGender ? draftFilters.gender : ''}
          options={genderOptions}
          disabled={!collectGender || genderOptions.length === 0}
          onChange={(value) => applyFilterImmediately('gender', value)}
        />
        <input
          className="leaderboard-filter-control leaderboard-bib-input"
          type="text"
          inputMode="numeric"
          placeholder="배번호를 입력하세요"
          value={draftFilters.bibKeyword}
          onChange={(event) => updateDraftFilter('bibKeyword', event.target.value)}
        />
        <button
          className="leaderboard-search-button"
          type="submit"
          disabled={isSearchDisabled}
          title={!draftFilters.category ? '종목을 선택해 주세요.' : undefined}
        >
          검색
        </button>
        <button
          className="leaderboard-reset-button"
          type="button"
          disabled={isFetching}
          onClick={handleResetFilters}
        >
          {"\uCD08\uAE30\uD654"}
        </button>
      </form>

      {isError && (
        <div className="leaderboard-message leaderboard-error" role="alert">
          {errorMessage}
        </div>
      )}

      {tableContent}
    </div>
  );
}

export default LeaderboardDashboard;
